import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import {
  Badge,
  BadgeColorPalette,
  Box,
  Center,
  Grid,
  Stack,
  Text,
  theme,
} from '@lojinha/design-system'
import { testId } from '@lojinha/helpers'
import {
  FirstOfferReplacementSettings,
  MarketableKind,
  Maybe,
} from '@lojinha/palantir'
import { AddToCartButton } from '../../../components/add-to-cart-button'
import { BadgesGroup } from '../../../components/product-card/components/badges-group'
import { Price } from '../../../components/product-card/components/price'
import { t } from '../../../dictionary'
import { getDiscountPercentage } from '../../../helpers/getDiscountPercentage'
import getPricePerMeal from '../../../helpers/getPricePerMeal'
import { OriginList } from '../../../modules/display/types'
import { DisplayItems } from '../../../modules/page-content/page-content.types'
import { ItemImage } from './item-image'

type Props = {
  item: DisplayItems
}

const Container = styled(Box)`
  background-color: ${theme.colors.gray100};

  &:last-child {
    margin-bottom: 2px;
  }
`

export const Summary = ({ item }: Props) => {
  const [firstOfferReplacement, setFirstOfferReplacement] = useState<
    Maybe<FirstOfferReplacementSettings>
  >()

  useEffect(() => {
    setFirstOfferReplacement(item.firstOfferReplacement)
  }, [item.firstOfferReplacement])

  const mealDescription =
    item.kind === 'MEAL'
      ? {
          testId: 'summary-meal-sideProducts',
          text: `${item.subtitle?.replace(' +', ',')}.`,
          ariaLabel: `${item.subtitle?.replace(' +', ',')}.`,
        }
      : null

  const itemDescription =
    item.kind === 'KIT'
      ? {
          testId: 'summary-description',
          text: item.description,
          ariaLabel: item.description,
        }
      : mealDescription

  function renderPriceInfo(item: DisplayItems) {
    const itemExtraInfo =
      item.kind === 'KIT'
        ? {
            testId: 'summary-kit-pricePerMeal',
            text: t.pricePerMealLabel(
              Math.round((item.price || 0) / (item.totalMealsCount || 1)) ?? 0
            ),
            ariaLabel: t.pricePerMealAriaLabel(
              Math.round((item.price || 0) / (item.totalMealsCount || 1)) ?? 0
            ),
          }
        : {
            testId: 'summary-weight',
            text: t.weightAndUnit(item.weight, item.unit),
            ariaLabel: t.weightAriaLabel(item.weight?.toString() ?? ''),
          }
    const itemDays = item.days?.length ?? 0
    const itemHasExtraInfo = item.kind !== 'KIT' || itemDays !== 1
    const itemExtraInfoBlock = itemHasExtraInfo && (
      <Text
        isBold
        textSize={['x14', 'x2']}
        aria-label={itemExtraInfo.ariaLabel}
        {...testId(itemExtraInfo.testId)}
      >
        {itemExtraInfo.text}
      </Text>
    )

    return (
      <Stack layout="vertical" margin="x8" key="item-price">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Price price={item.price || 0} priceFrom={item.priceFrom} isBold />
          {itemExtraInfoBlock}
        </Box>
      </Stack>
    )
  }

  function renderDiscountPercentage(discount: number) {
    if (!discount) {
      return
    }

    return (
      <Text
        color="error"
        textSize="x1"
        isBrand
        textTransform="uppercase"
        isBold
        whiteSpace="nowrap"
        htmlAttrs={{
          style: { whiteSpaceCollapse: 'preserve' },
        }}
      >
        {` • ${discount}% OFF`}
      </Text>
    )
  }

  function renderConditionalPriceInfo(
    firstOfferReplacement: FirstOfferReplacementSettings
  ) {
    const discount = getDiscountPercentage(
      firstOfferReplacement?.firstBuy.price,
      firstOfferReplacement?.rebuy.priceFrom
    )
    return (
      <Stack layout="vertical" margin="x8" key="item-conditional-price">
        <Box
          borderColor="error"
          borderStyle="dashed"
          borderWidth={1}
          marginBottom="x12"
          padding={'x4'}
        >
          <Stack layout="horizontal" margin="x0">
            <Badge
              key={'badge.content'}
              text={'Oferta 1ª compra'}
              colorPalette={BadgeColorPalette.Featured}
            />
            {renderDiscountPercentage(discount)}
          </Stack>
          <Stack
            layout="horizontal"
            htmlAttrs={{
              style: {
                justifyContent: 'space-between',
                marginTop: '5px',
              },
            }}
            alignItems="flex-end"
            margin="x4"
          >
            <Price
              price={firstOfferReplacement!.firstBuy.price}
              priceFrom={0}
              isBold
            />
            <Text isBold textSize={['x14', 'x2']}>
              {t.pricePerMealLabel(
                getPricePerMeal(
                  firstOfferReplacement!.firstBuy.price,
                  firstOfferReplacement!.firstBuy.totalMealsCount
                )
              )}
            </Text>
          </Stack>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
          padding={'x4'}
        >
          <Price
            price={firstOfferReplacement!.rebuy.price ?? 0}
            priceFrom={firstOfferReplacement!.rebuy.priceFrom}
            isBold
          />
          <Text isBold textSize={['x14', 'x2']}>
            {t.pricePerMealLabel(
              getPricePerMeal(
                firstOfferReplacement!.rebuy.price,
                firstOfferReplacement!.rebuy.totalMealsCount
              )
            )}
          </Text>
        </Box>
      </Stack>
    )
  }

  return (
    <Container
      display="flex"
      width="100%"
      minHeight={['492px', '492px', '532px']}
      alignItems="center"
      overflow="hidden"
      justifyContent="center"
    >
      <Center htmlAttrs={{ style: { width: '100%' } }}>
        <Grid
          gridTemplateColumns={[
            'repeat(24, 1fr)',
            'repeat(24, 1fr)',
            'repeat(12, 1fr)',
          ]}
          htmlAttrs={{ style: { width: '100%' } }}
        >
          <Grid gridColumn={['2 / 24', '2 / 24', '2 / 7']}>
            <ItemImage itemImageUrl={item.imageUrl} />
          </Grid>

          <Grid gridColumn={['2 / 24', '2 / 24', '8 / 11']}>
            <Stack
              layout="vertical"
              margin="x16"
              htmlAttrs={{
                style: { alignSelf: 'center' },
              }}
            >
              <Box height={theme.space.x16} marginTop={['x16', 'x16', 'x0']}>
                {item.itemTags.nodes && (
                  <BadgesGroup badges={item.itemTags.nodes} />
                )}
              </Box>

              <Stack layout="vertical" margin="x8">
                <Text
                  isBrand
                  textSize={['x2', 'x3']}
                  textTransform="uppercase"
                  aria-label={item.name}
                >
                  {item.mainProduct ?? item.name}
                </Text>

                {itemDescription && (
                  <Text
                    textSize={['x14', 'x2']}
                    color={'gray700'}
                    aria-label={itemDescription.ariaLabel}
                    {...testId(itemDescription.testId)}
                  >
                    {itemDescription.text}
                  </Text>
                )}
              </Stack>

              {firstOfferReplacement &&
                renderConditionalPriceInfo(firstOfferReplacement)}
              {!firstOfferReplacement && renderPriceInfo(item)}

              <AddToCartButton
                isBig
                isHover
                eventList={
                  item.kind === 'KIT' ? OriginList.LIST : OriginList.CATEGORY
                }
                isAvailable={item.isAvailable}
                isAgeRestricted={item.isAgeRestricted}
                item={{
                  id: item.id,
                  name: item.name,
                  kind: item.kind as MarketableKind,
                  classification: item.classification,
                  price: item.price || 0,
                  priceFrom: item.priceFrom,
                  imageUrl: item.imageUrl,
                  quantity: 0,
                  sku: item.sku,
                  firstOfferReplacement: item.firstOfferReplacement,
                }}
              />
            </Stack>
          </Grid>
        </Grid>
      </Center>
    </Container>
  )
}

export default Summary
